"use client";

import Script from "next/script";
import { Suspense, useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";

interface GoogleAnalyticsProps {
  // GA4 Measurement ID (e.g. "G-XXXXXXXXXX"). Read from
  // NEXT_PUBLIC_GA_MEASUREMENT_ID in the root layout and passed in. The ID
  // is public by design — it ships in the browser — so NEXT_PUBLIC_ is
  // correct here and does not expose a secret.
  gaId: string;
}

type GtagWindow = Window & {
  gtag?: (...args: unknown[]) => void;
};

// Builds the GA-visible page path from the App Router location so each
// client-side navigation is reported as its own page_view. Search params
// are included (e.g. a directory filtered by ?topic=anxiety is a distinct
// view from the unfiltered directory).
function buildPagePath(pathname: string, search: string): string {
  return search ? `${pathname}?${search}` : pathname;
}

// Reports a page_view to GA4 on every client-side route change.
//
// The initial page load is tracked automatically by the gtag('config')
// call in the init <Script> below (GA4's default send_page_view). This
// effect therefore SKIPS its first run to avoid double-counting that
// initial view, and fires only on subsequent navigations using the
// documented SPA method — gtag('config', id, { page_path }).
function GoogleAnalyticsRouteTracker({ gaId }: GoogleAnalyticsProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const hasNavigatedRef = useRef(false);

  useEffect(() => {
    if (!gaId) return;

    // Skip the first invocation (initial mount) — gtag('config') already
    // sent the initial page_view. Only soft navigations reach the send.
    if (!hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      return;
    }

    const w = window as GtagWindow;
    if (typeof w.gtag !== "function") return;

    w.gtag("config", gaId, {
      page_path: buildPagePath(pathname, searchParams.toString()),
    });
  }, [gaId, pathname, searchParams]);

  return null;
}

// Loads Google Analytics 4 (gtag.js) once for the whole app and tracks a
// page_view on the initial load and on every client-side route change.
//
// Mounted once in the root layout, so a single tag covers every route —
// no per-page wiring is needed. When gaId is empty (e.g. local/dev builds
// that do not set NEXT_PUBLIC_GA_MEASUREMENT_ID) this renders nothing, so
// non-production traffic is never sent to the GA property.
export default function GoogleAnalytics({ gaId }: GoogleAnalyticsProps) {
  if (!gaId) return null;

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${gaId}`}
        strategy="afterInteractive"
        data-testid="ga-script"
      />
      <Script id="ga-init" strategy="afterInteractive" data-testid="ga-init">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${gaId}');
        `}
      </Script>
      <Suspense fallback={null}>
        <GoogleAnalyticsRouteTracker gaId={gaId} />
      </Suspense>
    </>
  );
}
